<script setup>
import { inject, ref, watch, computed } from 'vue'
import { FilterMatchMode } from '@primevue/core/api'
import { useConfirm } from 'primevue/useconfirm'
import { useRoute } from 'vue-router'
import { useObjectsStore } from '@/store/objects'
import SfIcon from '@/components/SfIcon.vue'
import { useDialog } from 'primevue/usedialog'
import NewMappingModal from './modals/NewMappingModal.vue'

const confirm = useConfirm()
const dialog = useDialog()
const windowTooSmall = inject('windowTooSmall')

const confirmDelete = (objToDelete) => {
  confirm.require({
    group: 'confirm-mapping-deletion',
    header: `Delete ${objToDelete} Mapping`,
    message: objToDelete,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    rejectProps: {
      label: 'Cancel',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Delete',
      severity: 'danger'
    },
    accept: async () => {
      await deleteItem(objToDelete)
    }
  })
}

const syncObject = (objectLabel, objToSync) => {
  confirm.require({
    group: 'confirm-sync-object',
    header: `Sync ${objectLabel} Mapping`,
    message: objectLabel,
    rejectLabel: 'Cancel',
    rejectProps: {
      label: 'Cancel',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Sync',
      severity: 'primary'
    },
    accept: async () => {
      await objectStore.syncObject(objToSync, syncAllRecords.value)
      syncAllRecords.value = false
    }
  })
}

const openNewMappingModal = () => {
  dialog.open(NewMappingModal, {
    props: {
      header: 'New SObject Mapping',
      style: {
        width: windowTooSmall.value ? '90vw' : '800px',
        height: windowTooSmall.value ? '100vh' : '80vh'
      },
      contentStyle: 'overflow-y: hidden; display: flex; flex-direction: column; flex-grow: 1',
      modal: true
    }
  })
}

const objectStore = useObjectsStore()

const filters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  name: { value: null, matchMode: FilterMatchMode.CONTAINS }
})

const deleteItem = async (objName) => {
  try {
    loading.value = true
    await objectStore.deleteObjectMappingFromDB(objName)
    objectStore.updateDbRows({ objectName: objName, count: 0 })
    await fetchData()
  } catch (err) {
    console.error(err)
    // TODO: manage error
  }
}

const updateSyncMinutes = async (objName, syncMinutes) => {
  try {
    await objectStore.updateSyncMinutes(objName, syncMinutes)
  } catch (err) {
    console.error(err)
    // TODO: manage error
  }
}

const stopSyncDisabled = ref(new Set())
const totalDbRows = computed(() => objectStore.mappedObjects.reduce((sum, x) => sum + Number(objectStore.numDbRows[x.objectName] ?? x.numDbRows), 0))

const updateStopSync = async (objName, stopSync) => {
  try {
    stopSyncDisabled.value.add(objName)
    await objectStore.updateStopSync(objName, stopSync)
    stopSyncDisabled.value.delete(objName)
  } catch (err) {
    console.error(err)
  }
}

const route = useRoute()
const loading = ref(true)
const post = ref(null)
const error = ref(null)
const syncAllRecords = ref(false)

// watch the params of the route to fetch the data again
watch(route, fetchData, { immediate: true })

async function fetchData () {
  error.value = post.value = null
  loading.value = true

  try {
    await objectStore.fetchMappedObjects()
  } catch (err) {
    console.error(err)
    error.value = err.toString()
  } finally {
    loading.value = false
  }
}
const mapStatusIcon = (status, objectName) => {
  if (['New', 'Enqueued', 'Processing'].includes(objectStore.syncStatus[objectName] ?? status)) return 'pi pi-spin pi-spinner'
  return ''
}

const mapStatusSeveriy = (status, objectName) => {
  status = objectStore.syncStatus[objectName] ?? status
  if (status === 'Completed') return 'success'
  if (status === 'Failed') return 'danger'
  if (['Skipped', 'Warning', 'Paused'].includes(status)) return 'warn'
  if (['New', 'Enqueued', 'Processing'].includes(status)) return 'info'
  return 'contrast'
}

const mapStatusValue = (status, objectName) => {
  status = objectStore.syncStatus[objectName] ?? status
  if (status === 'Completed') return 'OK'
  if (status === 'Failed') return 'KO'
  if (status === 'Skipped') return 'Skipped'
  if (status === 'Warning') return 'Warning'
  if (status === 'Paused') return 'Paused'
  if (['New', 'Enqueued', 'Processing'].includes(status)) {
    if (windowTooSmall.value) return ''
    return 'Processing'
  }
  return 'Initializing'
}
</script>

<template>
  <ConfirmDialog group="confirm-mapping-deletion">
    <template #message="slotProps">
      <div class="flex flex-col items-center w-full gap-4 border-b border-surface-200 dark:border-surface-700">
        <i
          :class="slotProps.message.icon"
          class="!text-6xl text-primary-500"
        />
        <p>Are you sure you want to delete <b>{{ slotProps.message.message }}</b> mapping?</p>
        <p>This operation <b><u>CANNOT</u></b> be undone.</p>
        <br>
      </div>
    </template>
  </ConfirmDialog>
  <ConfirmDialog group="confirm-sync-object">
    <template #message="slotProps">
      <div class="flex flex-col items-center w-full gap-4 border-b border-surface-200 dark:border-surface-700">
        <p>Are you sure you want to sync <b>{{ slotProps.message.message }}</b> mapping?</p>
        <p>Sync all the table <ToggleSwitch v-model="syncAllRecords" /></p>
        <br>
      </div>
    </template>
  </ConfirmDialog>
  <div class="card">
    <div class="flex flex-row lg:gap-0 gap-4 justify-between lg:items-center h-fit lg:h-12">
      <IconField>
        <InputIcon>
          <i class="pi pi-search" />
        </InputIcon>
        <InputText
          id="globalFilter"
          v-model="filters['global'].value"
          v-focus="!windowTooSmall"
          placeholder="Keyword Search"
        />
      </IconField>
      <div class="flex gap-2">
        <Button
          :label="`New${!windowTooSmall ? ' SObject Mapping' : ''}`"
          @click="openNewMappingModal()"
          @mouseover.once="objectStore.fetchAvailableObjects()"
        />
      </div>
    </div>
    <Divider v-if="!windowTooSmall" />
    <div
      v-if="loading"
      class="flex flex-1 w-full items-center justify-center"
    >
      <ProgressSpinner />
    </div>
    <div
      v-else
      class="flex-1 overflow-y-auto px-0 -mx-4 -mb-4 md:mb-0 md:mx-0 md:px-10 pt-4 md:pb-4 rounded-lg bg-[--p-content-background]"
    >
      <DataTable
        v-if="!windowTooSmall"
        v-model:filters="filters"
        scrollable
        scroll-height="flex"
        removable-sort
        :global-filter-fields="['objectName','objectLabel']"
        :value="objectStore.mappedObjects"
        table-style="min-width: 50rem;"
        sort-field="objectLabel"
        :sort-order="1"
      >
        <template #empty>
          No mappings found
        </template>
        <Column
          style="min-width: 10rem"
          field="code"
          sortable
          header="Status"
        >
          <template #body="{ data }">
            <Tag
              :icon="mapStatusIcon(data.status, data.objectName)"
              :severity="mapStatusSeveriy(data.status, data.objectName)"
              :value="mapStatusValue(data.status, data.objectName)"
            />
          </template>
        </Column>
        <Column
          field="objectLabel"
          sortable
          header="Object"
        >
          <template #body="{ data }">
            <div class="flex flex-row items-center gap-2">
              <SfIcon
                :icon="data.iconName"
                :color="data.iconColor"
              />
              <div>
                <RouterLink
                  :to="`/objects/${data.objectName}`"
                  class="text-indigo-600 dark:text-[#569cd6] underline"
                >
                  {{ data.objectLabel }}
                </RouterLink>
                <div class="text-sm text-surface-500 dark:text-surface-400">
                  {{ data.objectName }}
                </div>
              </div>
            </div>
          </template>
          <template #filter="{ filterModel }">
            <InputText
              v-model="filterModel.value"
              type="text"
              placeholder="Search by name"
            />
          </template>
        </Column>
        <Column header="Mapped Fields">
          <template #body="{ data }">
            {{ data.numFields?.toLocaleString('it-IT') }}
          </template>
        </Column>
        <Column header="Salesforce Rows">
          <template #body="{ data }">
            {{ Intl.NumberFormat().format(data.numSFRows) }}
          </template>
        </Column>
        <Column header="Database Rows">
          <template #body="{ data }">
            {{ Intl.NumberFormat().format(objectStore.numDbRows[data.objectName] ?? data.numDbRows) }}
          </template>
        </Column>
        <Column header="Sync Interval (mins)">
          <template #body="{ data, index }">
            <div class="w-20">
              <select
                :id="'syncMinutes' + index"
                v-model="data.syncMinutes"
                :aria-label="`Sync Interval for ${data.objectName}`"
                class="dark:bg-[--p-form-field-background]"
                @change="updateSyncMinutes(data.objectName, data.syncMinutes)"
              >
                <option
                  v-for="item in 10"
                  :key="item"
                >
                  {{ item }}
                </option>
              </select>
            </div>
          </template>
        </Column>
        <Column header="Actions">
          <template #body="{ data }">
            <div class="flex flex-row justify-center w-40">
              <Button
                v-tooltip.top="'Refresh Table'"
                :aria-label="`Refresh Table: ${data.objectName}`"
                :disabled="data.stopSync || stopSyncDisabled.has(data.objectName) || !['Completed','Failed', 'Warning'].includes(objectStore.syncStatus[data.objectName] ?? data.status)"
                icon="pi pi-sync"
                rounded
                text
                @click="syncObject(data.objectLabel, data.objectName)"
              />
              <Button
                v-if="!data.stopSync"
                v-tooltip.top="'Pause Sync'"
                :aria-label="`Pause sync of ${data.objectName}`"
                :disabled="stopSyncDisabled.has(data.objectName)"
                icon="pi pi-pause"
                rounded
                text
                @click="updateStopSync(data.objectName, true)"
              />
              <Button
                v-if="data.stopSync"
                v-tooltip.top="'Restart sync'"
                :aria-label="`Restart sync of ${data.objectName}`"
                :disabled="stopSyncDisabled.has(data.objectName)"
                icon="pi pi-play"
                rounded
                text
                @click="updateStopSync(data.objectName, false)"
              />
              <RouterLink
                v-slot="{ navigate }"
                :to="`/objects/${data.objectName}`"
                custom
              >
                <Button
                  v-tooltip.top="'Edit Object'"
                  :aria-label="`Edit Object: ${data.objectName}`"
                  icon="pi pi-pencil"
                  rounded
                  text
                  @click="navigate"
                />
              </RouterLink>
              <Button
                v-tooltip.top="'Delete Object'"
                :aria-label="`Delete Object: ${data.objectName}`"
                icon="pi pi-trash"
                rounded
                severity="danger"
                text
                @click="confirmDelete(data.objectName)"
              />
            </div>
          </template>
        </Column>
        <ColumnGroup type="footer">
          <Row>
            <Column footer="Totals:" />
            <Column :footer="Intl.NumberFormat().format(objectStore.mappedObjects.length) + ' Objects'" />
            <Column :footer="Intl.NumberFormat().format(objectStore.mappedObjects.reduce((sum, x) => sum + Number(x.numFields), 0))" />
            <Column :footer="Intl.NumberFormat().format(objectStore.mappedObjects.reduce((sum, x) => sum + x.numSFRows, 0))" />
            <Column
              :colspan="3"
              :footer="Intl.NumberFormat().format(totalDbRows)"
            />
          </Row>
        </ColumnGroup>
      </DataTable>
      <DataTable
        v-else
        v-model:filters="filters"
        scrollable
        scroll-height="flex"
        removable-sort
        :global-filter-fields="['objectName','objectLabel']"
        :value="objectStore.mappedObjects"
        sort-field="objectLabel"
        :sort-order="1"
      >
        <template #empty>
          No mappings found
        </template>
        <Column
          field="objectLabel"
          sortable
          header="Object"
        >
          <template #body="{ data }">
            <div class="flex items-center justify-between">
              <div class="flex flex-row items-center gap-2">
                <SfIcon
                  :icon="data.iconName"
                  :color="data.iconColor"
                />
                <div>
                  <RouterLink
                    :to="`/objects/${data.objectName}`"
                    class="text-indigo-600 dark:text-[#569cd6]"
                  >
                    {{ data.objectLabel }}
                  </RouterLink>
                  <div class="text-sm text-surface-500 dark:text-surface-400">
                    {{ data.objectName }}
                  </div>
                </div>
              </div>
              <Tag
                :icon="mapStatusIcon(data.status, data.objectName)"
                :severity="mapStatusSeveriy(data.status, data.objectName)"
                :value="mapStatusValue(data.status, data.objectName)"
              />
            </div>
          </template>
        </Column>
      </datatable>
    </div>
  </div>
</template>

<style scoped>
  .p-datatable-scrollable-table>.p-datatable-tfoot {
    bottom: -1px
  }
</style>
